'use client'

import { Button, ButtonWithIcon } from '@Components/button'
import Divider from '@Components/divider'
import { IconButton } from '@Components/iconButton'
import { ChevronLeft, ChevronRight, Globe, MapPin, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'

import Image from 'next/image'
import LayoutWithSpacing from '@Components/layout/layoutWithSpacing'
import SubmenuItem from './submenuItem'
import { NavbarResponse, Menu } from '../../apis/navbar'
import { useRouter } from 'next/router'

export interface NavigationMenuProps {
  menus: NavbarResponse['Menus']
  logo: NavbarResponse['Logo'] | null
}

export const DesktopMenu = ({ menus, logo }: NavigationMenuProps) => {
  const pathname = usePathname()
  const router = useRouter()
  const { i18n } = useTranslation()
  const [hoveredMenuId, setHoveredMenuId] = useState('')
  const [isShowSubMenu, setShowSubMenu] = useState<boolean>(false)
  const subMenus = menus?.find((menu) => menu?.Slug === hoveredMenuId)?.SubMenus

  const foundObject = menus.find((menu) => {
    if (menu.SubMenus.length > 0) {
      return menu.SubMenus.some((subMenu) => subMenu.Path === router.asPath)
    } else {
      return menu.Path === router.asPath
    }
  })
  const logoUrl = logo?.url || ''
  const handleClickMenu = (path?: string | null) => {
    if (!isShowSubMenu && path) {
      router.push(path)
    }
  }
  let navStyle = 'pt-4'
  let submenuContainer = 'opacity-0'
  let menuStyle =
    'bg-main-navigation-default backdrop-blur-[50px] rounded-main-navigation p-4'
  if (isShowSubMenu) {
    navStyle =
      'bg-content-on-color-background transition-all ease-in-out duration-300 pt-4 shadow-menu'
    submenuContainer =
      'transition-all ease-in-out duration-300 opacity-100 w-full bg-main-navigation-default'
  }
  return (
    <nav
      className={`${navStyle} fixed z-50 flex w-full flex-col items-center justify-center`}
      onMouseLeave={() => {
        setHoveredMenuId('')
        setShowSubMenu(false)
      }}
    >
      <LayoutWithSpacing className='relative w-full p-0 md:p-0'>
        <div
          className={`${menuStyle} z-50 flex h-[4rem] flex-row items-center justify-between gap-4 ${
            !isShowSubMenu && 'shadow-menu'
          }`}
        >
          <Link href='/' locale={i18n.language}>
            <Image
              className='mr-[1rem] h-[5rem] w-[5rem]'
              src={logoUrl}
              width={100}
              height={100}
              alt=''
            />
          </Link>

          {/* Desktop */}
          <div className='hidden w-full items-center justify-between gap-4 lg:flex xl:w-3/4'>
            <div className='flex items-center gap-2 transition duration-0 hover:duration-150'>
              {menus?.map((menu, index: number) => (
                <Button
                  key={`${menu.Title}-${index}`}
                  onClick={() => handleClickMenu(menu.Path)}
                  variant={
                    foundObject?.Slug === menu.Slug ? 'menu-active' : 'menu'
                  }
                  onMouseEnter={() => {
                    setShowSubMenu(
                      !!menu?.SubMenus && menu?.SubMenus?.length > 0
                    )
                    setHoveredMenuId(menu.Slug)
                  }}
                >
                  {menu.Title}
                </Button>
              ))}
            </div>
            <div className='flex gap-4 self-end'>
              <Link href='/find-station' locale={i18n.language}>
                <ButtonWithIcon
                  size='md'
                  variant='primary'
                  leftIcon={<MapPin />}
                  className='w-fit'
                  onMouseLeave={() => {
                    setHoveredMenuId('')
                    setShowSubMenu(false)
                  }}
                >
                  Find Station
                </ButtonWithIcon>
              </Link>
              <IconButton
                onMouseEnter={() => {
                  setShowSubMenu(true)
                  setHoveredMenuId('language')
                }}
                variant='general'
              >
                <Globe />
              </IconButton>
            </div>
          </div>
        </div>

        <div className={submenuContainer}>
          {isShowSubMenu && (
            <div className='w-full items-center justify-center gap-4 bg-content-on-color-background p-4 lg:flex'>
              {hoveredMenuId === 'language' ? (
                <div className='flex w-full flex-col items-end gap-4 self-end'>
                  <Link
                    href={pathname}
                    locale='th'
                    onClick={() => setShowSubMenu(false)}
                  >
                    <Button
                      size='md'
                      variant='menu'
                      className={`w-fit ${
                        i18n.language === 'th' && 'font-bold'
                      }`}
                    >
                      ภาษาไทย
                    </Button>
                  </Link>
                  <Link
                    href={pathname}
                    locale='en'
                    onClick={() => setShowSubMenu(false)}
                  >
                    <Button
                      size='md'
                      variant='menu'
                      className={`w-fit ${
                        i18n.language === 'en' && 'font-bold'
                      }`}
                    >
                      English
                    </Button>
                  </Link>
                </div>
              ) : (
                subMenus?.map((menu, index: number) => (
                  <SubmenuItem
                    key={index}
                    item={menu}
                    className='h-full w-1/4 self-start'
                  />
                ))
              )}
            </div>
          )}
        </div>
      </LayoutWithSpacing>
    </nav>
  )
}

export const MobileMenu = ({ menus, logo }: NavigationMenuProps) => {
  const pathname = usePathname()
  const router = useRouter()
  const { i18n } = useTranslation()
  const [isShowMenuMobile, setShowMenuMobile] = useState(false)
  const [menuSelected, setMenuSelected] = useState('')
  const logoUrl = logo?.url || ''
  const onCloseMenu = () => {
    setShowMenuMobile(false)
    setMenuSelected('')
  }

  const onSelectMenu = (menu: Menu) => {
    if (menu.SubMenus && menu.SubMenus.length > 0) {
      setMenuSelected(menu.Slug)
      return
    }
    if (menu.Path) {
      router.push(menu.Path)
      return
    }
  }

  useEffect(() => {
    if (isShowMenuMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [isShowMenuMobile])
  return (
    <>
      <nav
        className={`fixed top-2 z-50 flex w-full flex-col items-center justify-center `}
      >
        <div className='relative w-full'>
          <div className='flex w-full'>
            <div
              className={`mx-[1rem] flex h-[4rem] w-full flex-row items-center justify-between gap-4 rounded-main-navigation bg-main-navigation-default px-[1rem] py-[0.5rem] shadow-menu shadow-menu backdrop-blur-[50px]`}
            >
              <Link href='/' locale={i18n.language} aria-label='logo'>
                <Image
                  className='mr-[1rem] h-[5rem] w-[5rem]'
                  src={logoUrl}
                  width={100}
                  height={100}
                  alt=''
                />
              </Link>
              <div className=' float-right'>
                <Button
                  variant='secondary'
                  className='w-fit'
                  onClick={() => setShowMenuMobile(!isShowMenuMobile)}
                >
                  Menu
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div
        className={` transition-all duration-200 ease-in-out ${
          isShowMenuMobile ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Main Menu */}
        {isShowMenuMobile && !menuSelected && (
          <div className='fixed bottom-0 left-0 right-0 top-0 z-50 min-h-screen w-full overflow-scroll bg-white px-7 py-16 lg:hidden'>
            <div className='absolute right-2 top-2 w-full'>
              <IconButton
                className='fixed right-2 top-2 float-right mb-10'
                variant='light'
                onClick={onCloseMenu}
              >
                <X className='h-icon-lg w-icon-lg' />
              </IconButton>
            </div>
            <div className='mt-10 flex w-full flex-col justify-start gap-4'>
              <div className='flex flex-col justify-start gap-4 transition duration-0 hover:duration-150'>
                {menus?.map((menu, index: number) => (
                  <ButtonWithIcon
                    key={`${menu.Title}-${index}`}
                    variant='menu'
                    onClick={() => onSelectMenu(menu)}
                    className='w-fit cursor-default text-start'
                    rightIcon={
                      !!menu.SubMenus && menu.SubMenus.length > 0 ? (
                        <ChevronRight className='lg:hidden' />
                      ) : null
                    }
                  >
                    {menu.Title}
                  </ButtonWithIcon>
                ))}
              </div>
              <Divider />
              <Link href='/find-station' locale={i18n.language}>
                <ButtonWithIcon
                  size='md'
                  variant='primary'
                  leftIcon={<MapPin />}
                  className='w-fit'
                >
                  Find Station
                </ButtonWithIcon>
              </Link>
              <Divider />
              <div className='flex w-full flex-col items-start gap-4'>
                <Link
                  href={pathname}
                  locale='th'
                  onClick={() => setShowMenuMobile(false)}
                >
                  <Button
                    size='md'
                    variant='menu'
                    className={`w-fit ${i18n.language === 'th' && 'font-bold'}`}
                  >
                    ภาษาไทย
                  </Button>
                </Link>
                <Link
                  href={pathname}
                  locale='en'
                  onClick={() => setShowMenuMobile(false)}
                >
                  <Button
                    size='md'
                    variant='menu'
                    className={`w-fit ${i18n.language === 'en' && 'font-bold'}`}
                  >
                    English
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
        {/* End Main Menu */}

        {/* Sub Menu */}
        {!!menuSelected && (
          <div className='fixed bottom-0 left-0 right-0 top-0 z-50 min-h-screen w-full overflow-hidden bg-white px-7 py-16 lg:hidden'>
            <div className='w-full'>
              <IconButton
                className='absolute left-2 top-2 float-left mb-10'
                variant='light'
                onClick={() => setMenuSelected('')}
              >
                <ChevronLeft className='h-icon-lg w-icon-lg' />
              </IconButton>
              <IconButton
                className='absolute right-2 top-2 float-right mb-10'
                variant='light'
                onClick={onCloseMenu}
              >
                <X className='h-icon-lg w-icon-lg' />
              </IconButton>
            </div>
            <div className='mt-10 flex w-full flex-col justify-start gap-4'>
              <div className='flex flex-col items-start gap-4 transition duration-0 hover:duration-150'>
                {menus
                  ?.find((menu) => menu.Slug === menuSelected)
                  ?.SubMenus?.map((menu, index: number) => (
                    <SubmenuItem
                      className='w-full'
                      key={`${menu.Title}-${index}`}
                      item={menu}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}
        {/* End Sub Menu */}
      </div>
    </>
  )
}
